/**
 * 기관 관리(바우처 기관발행) 화면 공용 유틸 — 정본 docs/바우처_기관발행_강사화면.md §4~§6
 */
import { formatActivityDateTime } from '@/utils/datetime';

/* ───────── 판매 등급 ───────── */
export const GRADES = ['basic', 'standard', 'premium'];
const GRADE_LABEL = { basic: 'Basic', standard: 'Standard', premium: 'Premium' };
export const gradeLabel = (g) => GRADE_LABEL[String(g || '').toLowerCase()] || g || '-';

/** 교재 상세 `pricing_grade`("basic, standard, premium" — 공백 섞임) 또는 `allowed_grades` 배열 → 판매 등급 배열.
 *  비어 있으면 세 등급 전부(서버 규칙과 동일). 교재의 grade/grade_label 은 학습단계이므로 여기에 넣지 말 것. */
export const parsePricingGrades = (raw) => {
  const text = Array.isArray(raw) ? raw.join(',') : String(raw || '');
  const picked = text.split(',').map((s) => s.trim().toLowerCase()).filter((g) => GRADES.includes(g));
  return picked.length ? GRADES.filter((g) => picked.includes(g)) : [...GRADES];
};

/* ───────── 금액·날짜 ───────── */
/** 문자열 Decimal("9982.00") → "9,982.00 USD" — 소수 자릿수는 서버 값 그대로 */
export const fmtAmount = (v, currency = '') => {
  if (v === null || v === undefined || v === '') return '-';
  const s = String(v);
  const neg = s.startsWith('-');
  const [intPart, frac] = s.replace(/^[-+]/, '').split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const body = frac !== undefined ? `${grouped}.${frac}` : grouped;
  return `${neg ? '−' : ''}${body}${currency ? ' ' + currency : ''}`;
};
export const fmtSignedAmount = (v, currency = '') => {
  if (v === null || v === undefined || v === '') return '-';
  const s = String(v);
  return s.startsWith('-') ? fmtAmount(s, currency) : `+${fmtAmount(s, currency)}`;
};
export const isPositiveAmount = (v) => Number(v) > 0;
export const isNegativeAmount = (v) => Number(v) < 0;

/** UTC ISO(`Z`) → KST 표기(라벨 포함). 기존 학습활동 규칙과 동일 */
export const fmtDateTime = (v) => formatActivityDateTime(v, '-');
/** YYYY-MM-DD 는 그대로 */
export const fmtDate = (v) => (v ? String(v).slice(0, 10) : '-');
export const fmtRate = (v) => (v === null || v === undefined ? '-' : `${v}%`);
/** 오늘(KST) YYYY-MM-DD */
export const todayKst = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());

/* ───────── 상태 라벨 ───────── */
const pick = (map, key, field, fallback) => (map[key] ? map[key][field] : fallback);

export const classStatusLabel = (s) => ({ ongoing: '진행중', closed: '종료', cancelled: '취소' }[s] || s || '-');
export const classStatusKind = (s) => ({ ongoing: 'ok', closed: 'info', cancelled: 'err' }[s] || 'default');

const ASSIGNMENT_STATUS = { ACTIVE: { label: '진행 중', kind: 'ok' }, CLOSED: { label: '종료', kind: 'default' } };
export const assignmentStatusLabel = (s) => pick(ASSIGNMENT_STATUS, s, 'label', s || '-');
export const assignmentStatusKind = (s) => pick(ASSIGNMENT_STATUS, s, 'kind', 'default');

export const STUDENT_STATUS = {
  REGISTERED:    { label: '등록 완료',        kind: 'ok' },
  PROCESSING:    { label: '처리 중',          kind: 'warn' },
  AVAILABLE:     { label: '등록 가능',        kind: 'info' },
  OWNED:         { label: '이미 보유',        kind: 'default' },
  OTHER_VOUCHER: { label: '다른 바우처로 등록', kind: 'default' },
  LIMIT_REACHED: { label: '인원 상한 도달',    kind: 'warn' },
  CLOSED:        { label: '지정 종료',        kind: 'default' },
};
export const studentStatusLabel = (s) => pick(STUDENT_STATUS, s, 'label', s || '-');
export const studentStatusKind = (s) => pick(STUDENT_STATUS, s, 'kind', 'default');

const VOUCHER_STATUS = { PENDING: { label: '처리 중', kind: 'warn' }, REGISTERED: { label: '등록 완료', kind: 'ok' }, FAILED: { label: '실패', kind: 'err' } };
export const voucherStatusLabel = (s) => pick(VOUCHER_STATUS, s, 'label', s || '-');
export const voucherStatusKind = (s) => pick(VOUCHER_STATUS, s, 'kind', 'default');

const PURCHASE_STATUS = {
  REQUESTED: { label: '요청',     kind: 'warn' },
  CONFIRMED: { label: '확정',     kind: 'ok' },
  CANCELLED: { label: '철회',     kind: 'default' },
  REJECTED:  { label: '반려',     kind: 'err' },
  VOIDED:    { label: '확정 취소', kind: 'err' },
};
export const purchaseStatusLabel = (s) => pick(PURCHASE_STATUS, s, 'label', s || '-');
export const purchaseStatusKind = (s) => pick(PURCHASE_STATUS, s, 'kind', 'default');

export const LEDGER_TYPES = ['PURCHASE', 'PURCHASE_VOID', 'REGISTER', 'REGISTER_REVERSAL', 'WITHDRAW', 'ADJUST'];
const LEDGER_TYPE = {
  PURCHASE:          { label: '충전',      kind: 'ok' },
  PURCHASE_VOID:     { label: '충전 취소', kind: 'err' },
  REGISTER:          { label: '등록 차감', kind: 'info' },
  REGISTER_REVERSAL: { label: '차감 취소', kind: 'warn' },
  WITHDRAW:          { label: '회수',      kind: 'err' },
  ADJUST:            { label: '조정',      kind: 'default' },
};
export const ledgerTypeLabel = (s) => pick(LEDGER_TYPE, s, 'label', s || '-');
export const ledgerTypeKind = (s) => pick(LEDGER_TYPE, s, 'kind', 'default');

/* ───────── 오류 ───────── */
/** 오류 코드별 안내 문구 — 정본 §5 */
export const VOUCHER_ERROR_MESSAGES = {
  ORG_MEMBER_FORBIDDEN:            '이 기관의 Master 가 아닙니다.',
  ORG_INACTIVE:                    '기관이 비활성 상태입니다. 관리자에게 문의해 주세요.',
  ORG_MEMBER_NOT_INSTRUCTOR:       '강사 권한 승인을 먼저 받아야 합니다.',
  ORG_MEMBER_DUPLICATE:            '이미 등록된 강사입니다.',
  ORG_MEMBER_ROLE_FORBIDDEN:       'Master 는 관리자만 해제할 수 있습니다.',
  ORG_MEMBER_HAS_ACTIVE_VOUCHERS:  '이 강사의 클래스에 진행 중인 도서 지정이 있습니다.',
  VOUCHER_PRICE_NOT_FOUND:         '이 교재·등급의 가격표가 없습니다.',
  TEXTBOOK_NOT_AVAILABLE:          '공개되지 않았거나 비매품인 교재입니다.',
  CLASS_VOUCHER_GRADE_NOT_ALLOWED: '이 교재는 선택한 등급을 판매하지 않습니다.',
  CLASS_VOUCHER_LIMIT_BELOW_USED:  '인원 상한은 현재 등록 수보다 작게 줄일 수 없습니다.',
  CLASS_VOUCHER_CLASSROOM_INVALID: '기관 클래스가 아니거나 진행 중인 클래스가 아닙니다.',
  CLASS_VOUCHER_DATE_INVALID:      '등록 기한은 오늘(KST) 이후여야 합니다.',
  CLASS_VOUCHER_DUPLICATE:         '같은 도서의 진행 중 지정이 이미 있습니다.',
  CLASS_VOUCHER_CLOSED:            '종료된 지정은 수정할 수 없습니다.',
  VOUCHER_PURCHASE_NOT_REQUESTED:  '요청 상태의 구매만 철회할 수 있습니다.',
  VOUCHER_CURRENCY_UNKNOWN:        '등록되지 않은 통화입니다.',
  VOUCHER_AMOUNT_ZERO:             '금액은 0 보다 커야 합니다.',
  // 강사 검색(GET instructors/search) — 2026-09-22 2차 회신
  USER_NOT_FOUND:                  '해당 이메일의 활성 계정이 없습니다.',
  ORG_MEMBER_EMAIL_AMBIGUOUS:      '같은 이메일의 활성 계정이 둘 이상입니다. 관리자에게 문의하세요.',
  EMAIL_REQUIRED:                  '이메일을 입력하세요.',
  // 강사 등록 동의 코드(POST instructors/invites …) — 2026-09-23 통지
  ORG_INVITE_CODE_INVALID:         '코드가 맞지 않습니다.',
  ORG_INVITE_TOO_MANY_ATTEMPTS:    '잘못 입력한 횟수가 많아 잠겼습니다. 코드를 다시 보내 주세요.',
  ORG_INVITE_EXPIRED:              '동의 코드가 만료됐습니다(24시간). 등록을 다시 요청하세요.',
  ORG_INVITE_RATE_LIMITED:         '발송 한도(요청당 3회, 60초 간격)를 넘었습니다. 잠시 뒤 다시 시도하세요.',
  ORG_INVITE_NOT_PENDING:          '이미 확인·취소·만료된 요청입니다. 목록을 새로고침하세요.',
  ORG_INVITE_NOT_FOUND:            '이 기관의 등록 요청이 아닙니다. 목록을 새로고침하세요.',
  ORG_MEMBER_CONSENT_REQUIRED:     '강사 동의 코드 절차로만 등록할 수 있습니다.',
};

/** axios 오류 → { status, code, message, detail }. /voucher/org/* 는 detail 객체, /learning/* 는 문자열 */
export const voucherError = (err) => {
  const status = err && err.response ? err.response.status : null;
  const detail = err && err.response && err.response.data ? err.response.data.detail : undefined;
  if (detail && typeof detail === 'object') {
    return { status, code: detail.code || null, message: detail.message || '', detail };
  }
  return { status, code: null, message: typeof detail === 'string' ? detail : '', detail };
};

export const voucherErrorText = (err, fallback = '요청을 처리하지 못했습니다.') => {
  const e = voucherError(err);
  if (e.code && VOUCHER_ERROR_MESSAGES[e.code]) return VOUCHER_ERROR_MESSAGES[e.code];
  if (e.message) return e.message;
  if (e.status === 403) return '권한이 없습니다.';
  if (e.status === 404) return '대상을 찾을 수 없습니다.';
  return fallback;
};
